import React, { useEffect, useMemo, useState } from "react";
import {
  unstable_createMuiStrictModeTheme as createMuiTheme,
  responsiveFontSizes,
  ThemeProvider,
} from "@mui/material/styles";
import SettingsIcon from "@mui/icons-material/Settings";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import TollIcon from "@mui/icons-material/Toll";

import Popup from "./Popup";
import Login from "./Login";
import Settings from "./Settings";
import { currentUserAtom, logoutAtom } from "../support/atoms";
import { useAtom } from "jotai";
import { Account } from "@toolpad/core/Account";
import { useNavigate } from "react-router-dom/dist";
import Search from "./Search";
import Deployments from "./Deployments/index";
import NotificationsIcon from "@mui/icons-material/Notifications";
import TaskIcon from "@mui/icons-material/Task";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import PaidIcon from "@mui/icons-material/Paid";
import Approvals from "./Events/Approvals";
import ImportContactsIcon from "@mui/icons-material/ImportContacts";
import GpsNotFixedIcon from "@mui/icons-material/GpsNotFixed";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AddressBook from "./AddressBook";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import LibraryAddCheckIcon from "@mui/icons-material/LibraryAddCheck";
import PushNotificationButton from "./PushNotificationButton";
import Keywords from "./Keywords/Keywords";
import Transactions from "./Transactions/Transactions";
import TrackedTransactions from "./TrackedTransactions/TrackedTransactions";
import ChartPage from "./ChartPage";
import AdminUsers from "./AdminUsers";
import PeopleIcon from "@mui/icons-material/People";
import { Box } from "@mui/material";
import DashboardLayout from "./DashboardLayout";

function Redirect({ pathname, navigate }) {
  useEffect(() => {
    if (pathname === "/trackers") navigate("/trackers/txs");
    else navigate("/tokens");
  }, [pathname, navigate]);
  return <div></div>;
}

function PageContent({ pathname, navigate }) {
  const segments = pathname.split("/").filter(Boolean);
  const segment = segments[0];
  const subSegment = segments[1];

  if (segment === "admin" && subSegment === "users") {
    return <AdminUsers />;
  }

  switch (pathname) {
    case "/settings":
      return <Settings />;
    case "/chart":
      return <ChartPage />;
    case "/tokens":
      return <Deployments />;
    case "/address-book":
      return <AddressBook />;
    case "/events/liquidity-on-deployments":
      return (
        <Deployments
          key={"liq-on-deployment"}
          liqOnDeployments={true}
          topic={"LIQ_TOKEN"}
        />
      );
    case "/events/approvals":
      return <Approvals />;
    case "/events/standard-tokens":
      return (
        <Deployments
          key={"all"}
          topic={"STD_TOKEN"}
          checksums={[
            "0x8d9863b5",
            "0x6e821158",
            "0x926aa6bf",
            "0xc893a9e0",
            "0xe6be404a",
            "0x24ed5f2b",
            "0x7073bb8b",
            "0x18fd59e6",
          ]}
        />
      );
    case "/trackers/keywords":
      return <Keywords />;
    case "/trackers/txs":
      return <TrackedTransactions />;
    case "/events/no-dev-tokens":
      return (
        <Deployments
          key={"std"}
          topic={"DEV_TOKEN"}
          checksums={[
            "0x87f2f3cb",
            "0x274a2ee9",
            "0xf58bf2d6",
            "0xc8b2c0f4",
            "0x21522a4d",
            "0x9dc2c9d3",
            "0x30001db2",
          ]}
        />
      );
    default:
      return <Redirect pathname={pathname} navigate={navigate} />;
  }
}

const Page = () => {
  const [currentUser] = useAtom(currentUserAtom);
  const [pathname, setPathname] = useState(window.location.pathname);
  const navigate = useNavigate();

  useEffect(() => {
    setPathname(window.location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.pathname]);

  const navigation = [
    {
      title: "Chart",
      icon: <ShowChartIcon />,
      segment: "chart",
    },
    // { title: "Contract", icon: <DescriptionIcon />, segment: "contract" },
    { kind: "divider" },
    {
      title: "Trackers",
      icon: <GpsNotFixedIcon />,
      segment: "trackers",
      children: [
        {
          segment: "txs",
          title: "Transactions",
          icon: <ReceiptIcon />,
        },
        {
          segment: "keywords",
          title: "Keywords",
          icon: <ManageSearchIcon />,
        },
      ],
    },
    { title: "Tokens", icon: <TollIcon />, segment: "tokens" },
    {
      title: "Events",
      icon: <NotificationsIcon />,
      segment: "events",
      children: [
        {
          segment: "approvals",
          title: "Approvals",
          icon: <DoneAllIcon />,
        },
        {
          segment: "liquidity-on-deployments",
          title: "Liquidity on Deployments",
          icon: <PaidIcon />,
        },
        {
          segment: "standard-tokens",
          title: "Standard Tokens",
          icon: <TaskIcon />,
        },
        {
          segment: "no-dev-tokens",
          title: "No-Dev Tokens",
          icon: <LibraryAddCheckIcon />,
        },
      ],
    },
    {
      title: "Address Book",
      icon: <ImportContactsIcon />,
      segment: "address-book",
    },
    { kind: "divider" },
    { title: "Settings", icon: <SettingsIcon />, segment: "settings" },
  ];
  if (currentUser?.role === "ADMIN") {
    navigation.push({ kind: "divider" });
    navigation.push({
      segment: "admin",
      title: "Admin",
      icon: <SettingsIcon />,
      children: [
        {
          segment: "users",
          title: "Users",
          icon: <PeopleIcon />,
        },
        {
          segment: "telegram",
          title: "Telegram",
          icon: <SettingsIcon />,
        },
        {
          segment: "connections",
          title: "Connections",
          icon: <SettingsIcon />,
        },
      ],
    });
  }

  return (
    <>
      <Popup />
      {currentUser ? (
        <DashboardLayout>
          <PushNotificationButton />
          <Box sx={{ height: "100%", width: "100%", p: 0 }}>
            <PageContent pathname={pathname} navigate={navigate} />
          </Box>
        </DashboardLayout>
      ) : (
        <Login />
      )}
    </>
  );
};

export default Page;
